import { getCurrentInstance, onMounted, ref, type Ref } from 'vue'

import ClubApplicationService from '@/services/clubs/clubApplicationService'
import { handleRequest } from '@/utils/handleRequest';

interface CacheEntry {
  clubApplication: Ref<any>
  loaded: boolean
  inFlight: Promise<any> | null
}

// Shared per club application id so the show/success route guards' fetch is
// reused by the mounted view instead of refetching the same record.
const cache = new Map<string, CacheEntry>()

function entryFor(id: string): CacheEntry {
  let entry = cache.get(id)
  if (!entry) {
    entry = { clubApplication: ref(null), loaded: false, inFlight: null }
    cache.set(id, entry)
  }
  return entry
}

// Route-guard policy for a single club application. The show endpoint itself
// calls `authorize club_application, :show?`, so a fetch failure (403)
// already reflects the access decision — the guard only needs to check
// whether the fetch succeeded.
//
// Also covers the draft-resume route: restore_draft authorizes via
// DraftPolicy#restore? (record.user_id == user.id), so a 403 there means the
// draftId in the URL belongs to someone else.
export function useClubApplicationRoutePolicy(id: string | number) {

  const entry = entryFor(String(id))
  const clubApplication = entry.clubApplication

  const fetchClubApplication = async ({ force = true } = {}) => {
    if (!force && entry.loaded) return { success: true, data: clubApplication.value }
    if (entry.inFlight) return await entry.inFlight

    entry.inFlight = handleRequest(
      () => ClubApplicationService.getClubApplication(id),
      {
        onSuccess: (response: any) => {
          clubApplication.value = response.data
          entry.loaded = true
        }
      }
    )

    try {
      return await entry.inFlight
    } finally {
      entry.inFlight = null
    }
  }

  const canShow = () => entry.loaded

  const canRestoreDraft = async () => {
    const result: any = await handleRequest(() => ClubApplicationService.restoreDraft(id))
    return result?.success !== false
  }

  // Auto-fetch only when used inside a component; route guards call
  // fetchClubApplication() explicitly and have no instance to mount against.
  if (getCurrentInstance()) {
    onMounted(async () => await fetchClubApplication({ force: false }))
  }

  return {
    clubApplication,
    fetchClubApplication,
    canShow,
    canRestoreDraft
  }
}
